import { useEffect, useMemo, useState } from 'react';
import { generateQueryString } from '@qlibs/react-components';
import type { BaseResponsePaginationProps } from '@qlibs/react-components';
import { httpRequest } from '../../../../helpers/api';
import {
  isClutchOperationModeDevice,
  OperationModeDetailProperties,
  OperationModeProperties,
} from '../../../operationMode/types/operationMode.type';
import {
  CLUTCH_OPERATION_MODE_KEYS,
  generateReportOperationModeSummary,
  getPresentOperationModeKeys,
} from '../helperFunction';
import useGroup from '../../../../zustand/useGroup';
import useAppliedReportFilter from '../../../../hooks/useAppliedReportFilter';
import useReportLog from '../../../../hooks/useReportLog';

export type OperationModeSummaryRow = {
  name: string;
  runningTime: number;
  consRate: number;
};

type OperationModeGroups = {
  engine: OperationModeDetailProperties[];
  clutch: OperationModeDetailProperties[];
};

const emptyOperationModeGroups: OperationModeGroups = {
  engine: [],
  clutch: [],
};

const emptyOperationModeSummary = {
  rows: [] as OperationModeSummaryRow[],
  totalRunningTime: 0,
  totalConsRate: 0,
};

export default function useOperationModeSummary() {
  const { logs } = useReportLog();
  const { filter, timezone } = useAppliedReportFilter();
  const [loading, setLoading] = useState<boolean>(false);
  const [opModes, setOpModes] = useState<OperationModeGroups>(
    emptyOperationModeGroups,
  );

  useEffect(() => {
    if (!logs?.operation_mode || !Object.keys(logs.operation_mode).length) {
      setOpModes(emptyOperationModeGroups);
      setLoading(false);
      return;
    }

    let ignore = false;

    async function fetchOperationMode() {
      try {
        setLoading(true);
        setOpModes(emptyOperationModeGroups);
        const devicesIds = new Set<string>();
        Object.values(logs.operation_mode!).forEach((bucket) => {
          ['port', 'starboard', ...CLUTCH_OPERATION_MODE_KEYS].forEach((key) => {
            const deviceUniqueId = bucket?.[key]?.deviceUniqueId;
            if (deviceUniqueId) devicesIds.add(deviceUniqueId);
          });
        });

        if (!devicesIds.size) return;

        const res = await httpRequest.get<
          BaseResponsePaginationProps<OperationModeProperties>
        >(
          `/operation-mode/data-constant/by-devices${generateQueryString({
            filterDevicesIds: Array.from(devicesIds),
            limit: 100,
          })}`,
        );

        if (ignore) return;

        const groups: OperationModeGroups = {
          engine: [],
          clutch: [],
        };
        res.data.payload.results.forEach((item) => {
          const details = item.operationModeDetails || [];
          if (isClutchOperationModeDevice(item.deviceId)) {
            groups.clutch.push(...details);
          } else {
            groups.engine.push(...details);
          }
        });
        setOpModes(groups);
      } catch (error) {
        if (!ignore) {
          setOpModes(emptyOperationModeGroups);
          console.error('Failed get operation mode details', error);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    fetchOperationMode();

    return () => {
      ignore = true;
    };
  }, [logs]);

  return useMemo(() => {
    const summarize = (
      details: OperationModeDetailProperties[],
      engines?: string[],
    ) => {
      if (!details.length || (engines && !engines.length)) {
        return emptyOperationModeSummary;
      }

      const rows = generateReportOperationModeSummary({
        startAt: filter.startAt,
        endAt: filter.endAt,
        interval: filter.interval,
        logs,
        opModes: details,
        timezone,
        engines,
      });

      return {
        rows,
        totalRunningTime: rows.reduce((acc, curr) => acc + curr.runningTime, 0),
        totalConsRate: rows.reduce((acc, curr) => acc + curr.consRate, 0),
      };
    };

    const clutchKeys = getPresentOperationModeKeys(
      logs?.operation_mode,
      CLUTCH_OPERATION_MODE_KEYS,
    );

    return {
      loading,
      ...summarize(opModes.engine),
      clutch: summarize(opModes.clutch, clutchKeys),
    };
  }, [
    filter.endAt,
    filter.interval,
    filter.startAt,
    loading,
    logs,
    opModes,
    timezone,
  ]);
}
